import { domainConfigs, defaultConfig } from "@/sites/config/domainConfigs";
import { stripLocaleFromPath } from "@/utils/locale";
import { DomainRouteConfig } from "@/sites/types";

export class DomainConfigManager {
  private static configCache = new Map<string, DomainRouteConfig>();

  /**
   * Gets the configuration for a given hostname, using a cache.
   * @param hostname
   * @returns the corresponding domain configuration.
   */
  public static get(hostname: string): DomainRouteConfig {
    if (this.configCache.has(hostname)) {
      return this.configCache.get(hostname)!;
    }

    const foundConfig = Object.entries(domainConfigs).find(([domain]) =>
      hostname.includes(domain)
    )?.[1];

    const config = foundConfig ?? defaultConfig;

    this.configCache.set(hostname, config);

    return config;
  }

  /**
   * Clears the configuration cache.
   */
  public static clearCache(): void {
    this.configCache.clear();
  }

  /**
   * Controls whether if a route requires authentication for a specific domain.
   * @param hostname
   * @param pathname
   * @returns `true` if the route is not public and requires authentication.
   */
  public static requiresAuth(hostname: string, pathname: string): boolean {
    const config = this.get(hostname);
    if (!config.requiresAuth) {
      return false;
    }
    return !DomainConfigManager.isPublicRoute(hostname, pathname);
  }

  /**
   * Determines whether if a route is public for a specific domain..
   * @param hostname
   * @param pathname
   * @returns `true` if the route matches one of the public routes.
   */
  public static isPublicRoute(hostname: string, pathname: string): boolean {
    const config = this.get(hostname);
    return config.publicRoutes.some((route) => {
      const strippedLocalePathname = stripLocaleFromPath(pathname);
      return strippedLocalePathname === route || strippedLocalePathname.startsWith(`${route}/`);
    });
  }
}
